<script setup>
// DEV-ONLY: tymczasowy switcher kont do lokalnego developmentu.
// Renderowany wyłącznie pod `npm run dev` (import.meta.env.DEV — gate w App.vue),
// backend dodatkowo wymaga APP_ENV=local. DO USUNIĘCIA przed handoffem
// (razem z app/Http/Controllers/Dev/ i trasami /dev/* w routes/api.php).
import { ref } from 'vue';
import { UserRound, X } from '@lucide/vue';
import api from '@/services/api';
import { useAuthStore } from '@/stores/auth';

const auth     = useAuthStore();
const open     = ref(false);
const loading  = ref(false);
const switching = ref(null);
const accounts = ref([]);

async function toggle() {
    open.value = !open.value;
    if (open.value && !accounts.value.length) {
        loading.value = true;
        try {
            const { data } = await api.get('/dev/accounts');
            accounts.value = data;
        } finally {
            loading.value = false;
        }
    }
}

async function switchTo(account) {
    if (switching.value) return;
    switching.value = account.id;
    try {
        const { data } = await api.post('/dev/accounts/impersonate', { user_id: account.id });
        localStorage.setItem('auth_token', data.token);
        // Pełny reload — czysty stan wszystkich store'ów pod nową tożsamością.
        window.location.href = '/';
    } catch {
        switching.value = null;
    }
}
</script>

<template>
    <div class="fixed bottom-4 left-4 z-[100] font-sans">
        <!-- Panel -->
        <div
            v-if="open"
            class="mb-2 w-72 overflow-hidden rounded-lg border border-warning/40 bg-card shadow-xl"
        >
            <div class="flex items-center justify-between border-b border-warning/40 bg-warning/10 px-3 py-2">
                <p class="text-xs font-semibold text-warning">DEV · Przełącznik kont</p>
                <button class="text-muted-foreground hover:text-foreground" @click="open = false">
                    <X class="h-3.5 w-3.5" />
                </button>
            </div>
            <div v-if="loading" class="px-3 py-4 text-center text-xs text-muted-foreground">Ładowanie…</div>
            <div v-else class="max-h-80 overflow-y-auto">
                <button
                    v-for="account in accounts"
                    :key="account.id"
                    class="flex w-full items-center gap-2.5 border-b px-3 py-2 text-left transition-colors last:border-0 hover:bg-muted/40 disabled:opacity-50"
                    :disabled="!!switching"
                    @click="switchTo(account)"
                >
                    <span
                        class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold uppercase"
                        :class="account.id === auth.user?.id ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'"
                    >{{ account.name.charAt(0) }}</span>
                    <span class="min-w-0 flex-1">
                        <span class="block truncate text-xs font-medium">
                            {{ account.name }}
                            <span v-if="account.id === auth.user?.id" class="text-primary">·</span>
                        </span>
                        <span class="block truncate text-xs text-muted-foreground">{{ account.role_label }} · {{ account.email }}</span>
                    </span>
                    <span v-if="switching === account.id" class="text-[10px] text-muted-foreground">…</span>
                </button>
            </div>
        </div>

        <!-- Pływający przycisk -->
        <button
            class="flex h-9 items-center gap-1.5 rounded-full border border-dashed border-warning/60 bg-card px-3 text-xs font-medium text-warning shadow-lg transition-colors hover:bg-warning/10"
            title="DEV: przełącz konto (tymczasowe narzędzie deweloperskie)"
            @click="toggle"
        >
            <UserRound class="h-3.5 w-3.5" />
            DEV
        </button>
    </div>
</template>
